import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  type LegislationVersion,
  type TextDiff,
  fetchLegislationComparison,
  fetchLegislationVersions,
} from '@/lib/legislationVersionClient';
import { DialogTrigger } from '@radix-ui/react-dialog';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef, useState } from 'react';
import ComparisonContent from './legislation-comparison/ComparisonContent';
import ErrorDisplay from './legislation-comparison/ErrorDisplay';
import VersionSelector from './legislation-comparison/VersionSelector';
import DiffStyles from './legislation-comparison/styles';
import { calculateStats, formatDate } from './legislation-comparison/utils';

interface LegislationVersionComparisonProps {
  iri: string;
  elementId?: string;
  onVersionCountChange?: (count: number) => void;
  isVisible?: boolean;
  open?: boolean;
  setOpen?: (open: boolean) => void;
}

export function LegislationVersionComparison({
  iri,
  elementId,
  onVersionCountChange,
  isVisible = false,
  open,
  setOpen,
}: LegislationVersionComparisonProps) {
  const [isOpenInternal, setIsOpenInternal] = useState(false);
  const isOpenValue = open !== undefined ? open : isOpenInternal;
  const setIsOpenValue = setOpen || setIsOpenInternal;

  const [versions, setVersions] = useState<LegislationVersion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedVersion2, setSelectedVersion2] = useState<string>('');
  const [comparison, setComparison] = useState<TextDiff[]>([]);
  const [isComparing, setIsComparing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [diffView, setDiffView] = useState<'git' | 'split' | 'unified'>(
    'split'
  );
  const [versionStats, setVersionStats] = useState({
    added: 0,
    removed: 0,
    unchanged: 0,
  });

  const t = useTranslations('documentVersionComparison');

  // Use a ref to track the last fetch parameters to prevent duplicate calls
  const lastFetchParams = useRef<{
    iri: string;
    elementId?: string | undefined;
  } | null>(null);

  // Memoized function to fetch versions
  const handleFetchVersions = useCallback(async () => {
    if (!iri) return;

    // Prevent duplicate calls for the same parameters
    const currentParams = { iri, elementId };
    if (
      lastFetchParams.current &&
      lastFetchParams.current.iri === iri &&
      lastFetchParams.current.elementId === elementId
    ) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Update last fetch params to prevent duplicates
      lastFetchParams.current = currentParams;

      // Call the client-side function (with built-in caching)
      const data = await fetchLegislationVersions(iri, elementId);

      if (Array.isArray(data) && data.length > 0) {
        // Filter out the current IRI from the versions for Version 2 selection
        const filteredVersions = data.filter((version) => version.iri !== iri);
        setVersions(filteredVersions);

        // Report version count to parent component
        if (onVersionCountChange) {
          onVersionCountChange(filteredVersions.length);
        }

        // Set default selection for Version 2 only if not already set
        const firstFiltered = filteredVersions[0];
        const firstData = data[0];
        if (filteredVersions.length > 0 && !selectedVersion2 && firstFiltered) {
          setSelectedVersion2(firstFiltered.iri);
        } else if (data.length > 0 && !selectedVersion2 && firstData) {
          // Fallback to the first version if no other versions are available
          setSelectedVersion2(firstData.iri);
        }
      } else {
        setError('No versions found for this document');
        if (onVersionCountChange) {
          onVersionCountChange(0);
        }
      }
    } catch (error) {
      console.error('Error fetching versions:', error);
      setError(
        error instanceof Error ? error.message : 'Failed to fetch versions'
      );
      if (onVersionCountChange) {
        onVersionCountChange(0);
      }
    } finally {
      setIsLoading(false);
    }
  }, [iri, elementId, onVersionCountChange, selectedVersion2]);

  // Memoized function to fetch comparison with debouncing
  const handleFetchComparison = useCallback(async () => {
    if (!iri || !selectedVersion2) {
      setComparison([]);
      return;
    }

    setIsComparing(true);
    setError(null);

    try {
      // Use debounced function for better UX
      const data = await fetchLegislationComparison(
        iri,
        selectedVersion2,
        elementId ?? ''
      );

      console.log('🔍 COMPARISON API RESPONSE:');
      console.log('  - Full data:', data);
      console.log('  - Data length:', data?.length);
      console.log('  - First item:', data?.[0]);

      if (data?.[0]) {
        console.log('  - porovnavane.text:', data[0].porovnavane?.text);
        console.log('  - aktualne.text:', data[0].aktualne?.text);
        console.log(
          '  - porovnavane.text length:',
          data[0].porovnavane?.text?.length
        );
        console.log(
          '  - aktualne.text length:',
          data[0].aktualne?.text?.length
        );
        console.log(
          '  - porovnavane.text type:',
          typeof data[0].porovnavane?.text
        );
        console.log('  - aktualne.text type:', typeof data[0].aktualne?.text);
      }

      if (Array.isArray(data) && data.length > 0) {
        setComparison(data);

        if (data[0]?.porovnavane && data[0]?.aktualne) {
          // Always call calculateStats if we have the data structure
          // calculateStats already handles empty text cases properly
          const stats = calculateStats(
            data[0].porovnavane.text || '',
            data[0].aktualne.text || ''
          );
          console.log('  - Calculated stats:', stats);
          console.log(
            '  - Parameter order: porovnavane (old) -> aktualne (new)'
          );
          console.log(
            '  - Old version (porovnavane):',
            data[0].porovnavane?.text?.length,
            'chars'
          );
          console.log(
            '  - New version (aktualne):',
            data[0].aktualne?.text?.length,
            'chars'
          );
          setVersionStats(stats);
        } else {
          console.log('  - Missing data structure, setting zero stats');
          setVersionStats({ added: 0, removed: 0, unchanged: 0 });
        }
      } else {
        // Handle empty data case gracefully
        console.warn('No comparison data available or empty data returned');
        setComparison([]);
        setError(
          'No comparison data available. The versions may be identical or there might be an issue with the comparison service.'
        );
      }
    } catch (error) {
      console.error('Error fetching comparison:', error);
      setError(
        error instanceof Error ? error.message : 'Failed to fetch comparison'
      );
      // Reset comparison data on error
      setComparison([]);
    } finally {
      setIsComparing(false);
    }
  }, [iri, selectedVersion2, elementId]);

  // Reset state when dialog closes (but keep versions cached)
  useEffect(() => {
    if (!isOpenValue) {
      setComparison([]);
      setError(null);
    }
  }, [isOpenValue]);

  // Optimized effect: Only fetch versions when dialog opens AND iri/elementId have changed
  useEffect(() => {
    if ((isOpenValue || isVisible) && iri) {
      handleFetchVersions();
    }
  }, [isOpenValue, isVisible, handleFetchVersions]);

  // Optimized effect: Fetch comparison with debounced calls
  useEffect(() => {
    if (iri && selectedVersion2 && isOpenValue) {
      // Add a small delay to debounce rapid version changes
      const timeoutId = setTimeout(() => {
        handleFetchComparison();
      }, 150);

      return () => clearTimeout(timeoutId);
    } else {
      setComparison([]);
    }
    return; // Explicit return for all code paths
  }, [selectedVersion2, iri, isOpenValue, handleFetchComparison]);

  // Reset state when IRI changes (but don't immediately trigger fetch)
  useEffect(() => {
    // When the IRI changes, reset version-specific state
    setSelectedVersion2('');
    setComparison([]);
    setError(null);

    // Reset the last fetch params to allow new fetch for new IRI
    lastFetchParams.current = null;
  }, [iri]);

  // Get display name for version
  const getVersionDisplayName = (versionIri: string) => {
    const version = versions.find((v) => v.iri === versionIri);
    if (!version) return versionIri;

    let displayName = '';

    if (version.caption) {
      displayName += version.caption;
    } else if (version.ucinnyOd) {
      displayName += `Effective from ${formatDate(version.ucinnyOd)}`;
    }

    if (version.ucinnyDo) {
      displayName += ` to ${formatDate(version.ucinnyDo)}`;
    }

    return displayName || versionIri;
  };

  return (
    <Dialog open={isOpenValue} onOpenChange={setIsOpenValue}>
      <DialogTrigger asChild>
        {/* A real <button>, not a <div>: DialogTrigger forwards type="button",
            aria-haspopup="dialog" and aria-expanded to its child, and none of
            those are valid on a bare div (#8708, axe aria-allowed-attr). The
            dialog is opened programmatically, so the trigger stays hidden. */}
        <button
          type='button'
          className='hidden'
          aria-hidden='true'
          tabIndex={-1}
        />
      </DialogTrigger>
      <DialogContent
        className='max-w-4xl max-h-[90vh] overflow-hidden flex flex-col'
        aria-describedby={undefined}
      >
        <DialogHeader className='pb-2'>
          <DialogTitle className='text-xl'>{t('title')}</DialogTitle>
        </DialogHeader>

        <ErrorDisplay message={error} />

        <VersionSelector
          versions={versions}
          selectedVersion={selectedVersion2}
          onVersionChange={setSelectedVersion2}
          isLoading={isLoading}
          currentVersionName={
            iri ? getVersionDisplayName(iri) : 'Current version'
          }
        />

        <div className='flex-1 overflow-y-auto py-4'>
          <ComparisonContent
            comparison={comparison}
            isLoading={isLoading}
            isComparing={isComparing}
            diffView={diffView}
            setDiffView={setDiffView}
            versionStats={versionStats}
            getVersionDisplayName={getVersionDisplayName}
            currentVersionIri={iri}
            selectedVersion2={selectedVersion2}
          />
        </div>

        <DialogFooter className='border-t border-line-default pt-4'>
          <Button variant='outline' onClick={() => setIsOpenValue(false)}>
            {t('close')}
          </Button>
        </DialogFooter>
      </DialogContent>

      <DiffStyles />
    </Dialog>
  );
}
